// Layer 3 — the Plant data, Signals and Excursions stage bodies under the
// Advisory strip (polish.P28a, #715; comp `.impeccable/mocks/decision/
// advisory-stages.html`, picked A with C's replay-window lanes).
// Pure projections of `flowLandingRead()`: every list here is the list its
// strip cell counts.

import type { Alarm, Case } from '@/versions/v1/schema/case';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { TagMapping } from '@/versions/v1/schema/tag-mapping';
import { diffMinutes, formatDayMonth, shiftOf } from '@/versions/v1/shared/clock.derive';
import { variableIdForAlarm } from '@/versions/v1/shared/DiagnosisEngine';
import { SUBPROCESSES } from '@/versions/v1/shared/bunker-hill-seed';
import { variableIdForCase } from '@/versions/v1/shared/threads';
import type { PipelineStageId } from './PipelineStrip.transform';
import {
  flowStamp,
  formatExcursionRule,
  formatPipelineSignalRole,
  heldMinutesForExcursions,
  type FlowLandingRead,
} from './FlowLanding.transform';

export type StageCaseLink = { caseId: string; code: string };

export type SignalBodyRow = {
  key: string;
  tag: string;
  name: string;
  rules: readonly string[];
  cadence: string;
  excursionCount: number;
  /** The monitored variable this row's mapping resolves to (`mv-…`). */
  variableId?: string;
  /** The DCS alarm the engine maps to this row's variable, when there is one. */
  dcsAlarmCode?: string;
  /**
   * Every open case the seed names on this row's variable, in Advisory queue
   * order (#758). Empty where the seed names none.
   */
  caseLinks: readonly StageCaseLink[];
};

export type ExcursionBodyRow = {
  id: string;
  tag: string;
  name: string;
  rule: string;
  heldMinutes: number;
  since: SeriesTime;
  /** Share of the longest held excursion in the list, 0–100. */
  heldPct: number;
  caseLink?: StageCaseLink;
};

export type ReplayLane = {
  tag: string;
  heldMinutes: number;
  /** One band per excursion on the instrument, from its start to the cursor. */
  bands: readonly { id: string; leftPct: number; beforeWindow: boolean }[];
  beforeWindow: boolean;
  /** DCS alarm raise marks on this instrument inside the window. */
  dcsMarks: readonly { code: string; leftPct: number }[];
};

export type ReplayLanes = {
  lanes: readonly ReplayLane[];
  windowStart: SeriesTime;
  cursor: SeriesTime;
  windowStartPct: number;
  shiftStartPct: number | null;
  startLabel: string;
  cursorLabel: string;
  rangeLabel: string;
};

export type OpenedThisShift = { count: number; since: string; detail: string };

export type PlantDataBodyRow = { key: string; tag: string; name: string; circuit: string; cadence: string };

export type DcsAlarmBodyRow = {
  id: string;
  code: string;
  priorityLabel: string;
  priority: Alarm['priority'];
  message: string;
  tag?: string;
  /** The monitored variable the engine maps this alarm to (`variableIdForAlarm`). */
  variableId?: string;
  raisedAt: string;
  state: Alarm['state'];
};

/**
 * The signal a tag carries, named from its role. The ball mill's roles carry
 * the plant's asset id (`bm-bearing-temp` → `BM01 bearing temp`), as the
 * grinding circuit names it (`Primary grinding (BM01 + CY01)`).
 */
export function signalName(role: string): string {
  return formatPipelineSignalRole(role).replace(/^Bm\b/, 'BM01');
}

/** Every monitored variable id follows its series (`rs-<x>` → `mv-<x>`). */
function variableIdForMapping(mapping: TagMapping | undefined): string | undefined {
  return mapping?.seriesId ? `mv-${mapping.seriesId.replace(/^rs-/, '')}` : undefined;
}

const OPEN_STAGES: ReadonlySet<PipelineStageId> = new Set(['cases', 'diagnosis', 'action']);

/**
 * A case link renders only where the case seed names the signal's variable
 * (adjudication 10): the variable's own excursion case first (`case-<mv>`),
 * then an open case whose variation event or alarm names the variable, then
 * any raised one. A case the cursor has not raised is never linked.
 *
 * The single link the Excursions body's Case column carries; Signals lists
 * every open case on the variable (`caseLinksForMapping`).
 */
export function caseLinkForMapping(
  mapping: TagMapping | undefined,
  cases: readonly Case[],
  caseStages: ReadonlyMap<string, PipelineStageId | null>,
): StageCaseLink | undefined {
  const variableId = variableIdForMapping(mapping);
  if (!variableId) return undefined;
  const raised = cases.filter((caseItem) => Boolean(caseStages.get(caseItem.id)));
  const named = raised.filter((caseItem) => variableIdForCase(caseItem) === variableId);
  const pick = raised.find((caseItem) => caseItem.id === `case-${variableId}`) ??
    named.find((caseItem) => OPEN_STAGES.has(caseStages.get(caseItem.id)!)) ??
    named[0];
  return pick ? { caseId: pick.id, code: pick.code ?? pick.id } : undefined;
}

/**
 * #758: every open case the seed names on this signal's variable, in the
 * Advisory queue order the Cases body lists (a case the cursor has not raised
 * is never linked; a named case past Action has left the queue). `queue` is
 * that body's own order, so a row's links read in the order the reader would
 * meet the cases; a named case missing from the queue keeps its seed order.
 */
export function caseLinksForMapping(
  mapping: TagMapping | undefined,
  cases: readonly Case[],
  caseStages: ReadonlyMap<string, PipelineStageId | null>,
  queue: readonly string[],
): StageCaseLink[] {
  const variableId = variableIdForMapping(mapping);
  if (!variableId) return [];
  const open = cases.filter((caseItem) =>
    variableIdForCase(caseItem) === variableId &&
    caseStages.has(caseItem.id) &&
    OPEN_STAGES.has(caseStages.get(caseItem.id)!));
  const rank = new Map(queue.map((caseId, index) => [caseId, index]));
  return open
    .map((caseItem, index) => ({ caseItem, rank: rank.get(caseItem.id) ?? queue.length + index }))
    .sort((left, right) => left.rank - right.rank)
    .map(({ caseItem }) => ({ caseId: caseItem.id, code: caseItem.code ?? caseItem.id }));
}

/** DCS alarms as the plant raised them (the advisory-origin row is a case, never an alarm here). */
export function dcsAlarmRows(alarms: readonly Alarm[], priorityLabel: (alarm: Alarm) => string): DcsAlarmBodyRow[] {
  return alarms
    .filter((alarm) => alarm.origin === 'dcs')
    .map((alarm) => ({
      id: alarm.id,
      code: alarm.code,
      priorityLabel: priorityLabel(alarm),
      priority: alarm.priority,
      message: alarm.message ?? alarm.code,
      tag: alarm.tag,
      variableId: variableIdForAlarm(alarm),
      raisedAt: alarm.raisedAt,
      state: alarm.state,
    }));
}

export function dcsAlarmMeta(rows: readonly DcsAlarmBodyRow[]): string {
  const active = rows.filter((row) => row.state === 'active').length;
  const acked = rows.filter((row) => row.state === 'acked').length;
  return [active ? `${active} active` : '', acked ? `${acked} acked` : ''].filter(Boolean).join(' · ') || `${rows.length} alarms`;
}

/**
 * The DCS alarm code for each monitored variable the plant already alarms, via
 * the engine's one alarm → variable mapping (`variableIdForAlarm`, the same
 * derivation the CRO excursion boundary uses). Tag equality is never the rule:
 * ZN-PH-01's own tag is AIC5002, which resolves through AT-150's `loopTag`.
 */
function dcsAlarmCodeByVariable(alarms: readonly Alarm[]): Map<string, string> {
  const byVariable = new Map<string, string>();
  for (const alarm of alarms) {
    const variableId = variableIdForAlarm(alarm);
    if (variableId && !byVariable.has(variableId)) byVariable.set(variableId, alarm.code);
  }
  return byVariable;
}

/**
 * The Signals list: the SPC signals the engine holds plus each recorded
 * fixture case's fired signal — exactly the rows the Signals cell counts.
 * Ranked by excursions, most first.
 */
export function signalBodyRows(
  read: Pick<FlowLandingRead, 'activeSignals' | 'fixtureSignals' | 'fixtureExcursions' | 'caseStages' | 'openCaseRows'>,
  cases: readonly Case[],
  alarms: readonly Alarm[],
): SignalBodyRow[] {
  const dcsByVariable = dcsAlarmCodeByVariable(alarms);
  // #758: the Cases body's own order, so a row's case links read in it.
  const queue = read.openCaseRows.map((row) => row.caseItem.id);
  const legacy: SignalBodyRow[] = read.activeSignals.map((signal) => {
    const events = signal.spc?.events ?? [];
    const variableId = variableIdForMapping(signal.mapping);
    return {
      key: signal.role,
      tag: signal.mapping.instrumentTag,
      name: signalName(signal.role),
      rules: [...new Set(events.map((event) => formatExcursionRule(event.rule)))],
      cadence: signal.mapping.cadence,
      excursionCount: events.length,
      variableId,
      dcsAlarmCode: variableId ? dcsByVariable.get(variableId) : undefined,
      caseLinks: caseLinksForMapping(signal.mapping, cases, read.caseStages, queue),
    };
  });
  const fixture: SignalBodyRow[] = read.fixtureSignals.map((signal) => {
    const variableId = variableIdForMapping(signal.mapping);
    return {
      key: `${signal.caseId}-${signal.tag}`,
      tag: signal.tag,
      name: signalName(signal.mapping.role),
      rules: signal.ruleLabels,
      cadence: signal.mapping.cadence,
      excursionCount: read.fixtureExcursions.filter((event) => event.tag === signal.tag).length,
      variableId,
      dcsAlarmCode: variableId ? dcsByVariable.get(variableId) : undefined,
      caseLinks: caseLinksForMapping(signal.mapping, cases, read.caseStages, queue),
    };
  });
  return [...legacy, ...fixture]
    .map((row, index) => ({ row, index }))
    .sort((left, right) => right.row.excursionCount - left.row.excursionCount || left.index - right.index)
    .map(({ row }) => row);
}

/**
 * Header meta beside the Signals title: the rank the list carries. The Picked
 * section binds "a panel head never restates its strip cell", so the head names
 * the order `signalBodyRows` sorts by — never the Signals or Excursions count.
 */
export function signalBodyMeta(rows: readonly SignalBodyRow[]): string {
  const ranked = rows.every(
    (row, index) => index === 0 || rows[index - 1]!.excursionCount >= row.excursionCount,
  );
  return ranked ? 'ranked by excursions' : 'engine order';
}

/**
 * The Excursions list: every active excursion the Excursions cell counts
 * (SPC excursions plus the recorded fixture's), longest held first.
 */
export function excursionBodyRows(
  read: Pick<FlowLandingRead, 'excursionRows' | 'fixtureExcursions' | 'caseStages'>,
  tagMappings: readonly TagMapping[],
  cases: readonly Case[],
): ExcursionBodyRow[] {
  const legacy = read.excursionRows.map(({ event, instrumentTag }) => {
    const mapping = tagMappings.find((row) => row.readingId === event.readingId);
    return {
      id: event.id,
      tag: instrumentTag,
      name: mapping ? signalName(mapping.role) : instrumentTag,
      rule: formatExcursionRule(event.rule),
      heldMinutes: heldMinutesForExcursions([event]),
      since: event.detectedAt,
      caseLink: caseLinkForMapping(mapping, cases, read.caseStages),
    };
  });
  const fixture = read.fixtureExcursions.map((event) => ({
    id: event.id,
    tag: event.tag,
    name: event.mapping ? signalName(event.mapping.role) : event.tag,
    rule: event.rule,
    heldMinutes: event.heldMinutes,
    since: event.sinceAt,
    caseLink: caseLinkForMapping(event.mapping, cases, read.caseStages),
  }));
  const rows = [...legacy, ...fixture]
    .map((row, index) => ({ row, index }))
    .sort((left, right) => right.row.heldMinutes - left.row.heldMinutes || left.index - right.index)
    .map(({ row }) => row);
  const longest = rows.reduce((max, row) => Math.max(max, row.heldMinutes), 0);
  return rows.map((row) => ({ ...row, heldPct: longest > 0 ? (row.heldMinutes / longest) * 100 : 0 }));
}

export function stampLabel(at: SeriesTime): string {
  return `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

/** Share of the track reserved, left of the window start, for excursions that began before it. */
export const LANE_GUTTER_PCT = 18;

/**
 * C's replay-window lanes, compact: one lane per instrument across the
 * replay window up to the cursor, a band per excursion from its start to the
 * cursor, a ◂ start for excursions that began before the window, and the DCS
 * alarm raised on the instrument as a tick.
 */
export function replayLanes(
  rows: readonly ExcursionBodyRow[],
  alarms: readonly Alarm[],
  windowStart: SeriesTime,
  cursor: SeriesTime,
): ReplayLanes {
  const span = Math.max(1, diffMinutes(cursor, windowStart));
  const pct = (at: SeriesTime) => {
    const offset = diffMinutes(at, windowStart);
    if (offset < 0) return 0;
    return Math.min(100, LANE_GUTTER_PCT + (100 - LANE_GUTTER_PCT) * (offset / span));
  };
  const tags = [...new Set(rows.map((row) => row.tag))];
  const lanes = tags.map((tag) => {
    const own = rows.filter((row) => row.tag === tag);
    const bands = own.map((row) => ({ id: row.id, leftPct: pct(row.since), beforeWindow: row.since < windowStart }));
    const dcsMarks = alarms
      .filter((alarm) => alarm.origin === 'dcs' && alarm.tag === tag)
      .flatMap((alarm) => {
        const at = flowStamp(alarm.raisedAt, cursor);
        return at && at >= windowStart && at <= cursor ? [{ code: alarm.code, leftPct: pct(at) }] : [];
      });
    return {
      tag,
      heldMinutes: own.reduce((max, row) => Math.max(max, row.heldMinutes), 0),
      bands,
      beforeWindow: bands.some((band) => band.beforeWindow),
      dcsMarks,
    };
  });
  const shiftStart = shiftOf(cursor).start;
  return {
    lanes,
    windowStart,
    cursor,
    windowStartPct: pct(windowStart),
    shiftStartPct: shiftStart > windowStart && shiftStart < cursor ? pct(shiftStart) : null,
    startLabel: formatDayMonth(windowStart),
    cursorLabel: stampLabel(cursor),
    rangeLabel: `${stampLabel(windowStart)} → ${cursor.slice(11, 16)}`,
  };
}

/** The excursions that opened since the running shift began, oldest first. */
export function openedThisShift(rows: readonly ExcursionBodyRow[], cursor: SeriesTime): OpenedThisShift {
  const start = shiftOf(cursor).start;
  const opened = rows
    .filter((row) => row.since >= start && row.since <= cursor)
    .sort((left, right) => left.since.localeCompare(right.since));
  const since = `since ${start.slice(11, 16)}`;
  const phrase = (row: ExcursionBodyRow) => `${row.rule.toLowerCase()} ${row.since.slice(11, 16)}`;
  const tags = new Set(opened.map((row) => row.tag));
  let detail = '';
  if (opened.length > 1 && tags.size === 1) {
    detail = `${opened.length === 2 ? 'both' : `all ${opened.length}`} ${opened[0]!.tag}: ${opened.map(phrase).join(', ')}`;
  } else if (opened.length > 0) {
    detail = opened.map((row) => `${row.tag} ${phrase(row)}`).join(', ');
  }
  return { count: opened.length, since, detail };
}

/** Short circuit name for a subprocess (`Pb/Ag flotation (rougher → …)` → `Pb/Ag flotation`). */
export function circuitShortName(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/, '');
}

/** Plant data's circuit column names the subprocess each tag sits on. */
const SUBPROCESS_NAME = new Map(SUBPROCESSES.map((row) => [row.id, row.name]));

export function subprocessName(id: string): string | undefined {
  return SUBPROCESS_NAME.get(id);
}

export function plantDataBodyRows(
  rows: readonly TagMapping[],
  subprocessName: (id: string) => string | undefined,
): PlantDataBodyRow[] {
  return rows.map((row) => ({
    key: `${row.instrumentTag}-${row.role}`,
    tag: row.instrumentTag,
    name: signalName(row.role),
    circuit: circuitShortName(subprocessName(row.subprocessId) ?? row.subprocessId),
    cadence: row.cadence,
  }));
}

/** The table's own split by cadence, most first (the cell already says how many are mapped). */
export function plantDataMeta(rows: readonly PlantDataBodyRow[]): string {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(row.cadence, (counts.get(row.cadence) ?? 0) + 1);
  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1])
    .map(([cadence, count]) => `${count} ${cadence}`)
    .join(' · ');
}
